import React, { useState } from "react";
import { Plus, Trash2, GripVertical, Pencil, Check, X, Info, Heart, Loader2, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { translateFoodName } from "@/i18n/foodTranslations";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { computeDil } from "@/lib/dilFormula";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

export function calcMealDIL(entries) {
  if (!entries || entries.length === 0) return null;
  let totalDil = 0;
  for (const e of entries) {
    // iter410 — единен източник `lib/dilFormula.computeDil` (v2.0, coeff 0.69)
    totalDil += computeDil(e.protein || 0, e.carbs || 0, e.fiber || 0);
  }
  // Feb 2026 — Return Number (not String) so consumers can safely call
  // `.toFixed(1)` again for display. Previously `toFixed` returned a String
  // → MealLog.formatDilValue crash (`v.toFixed is not a function`) that
  // tripped ErrorBoundary and blanked the whole /dashboard/diary render.
  return Math.round(totalDil * 10) / 10;
}

function DraggableMealEntry({ entry, mealType, isDark, language, onDelete, onEdit }) {
  const bg = language === "bg";
  const { API } = useAuth();
  const [editingId, setEditingId] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [favSaving, setFavSaving] = useState(false);
  const [favSaved, setFavSaved] = useState(false);
  // iter460 — separate state for "save to Health Intelligence" (food_log_entry snapshot)
  const [hiSaving, setHiSaving] = useState(false);
  const [hiSaved, setHiSaved] = useState(false);

  // iter420.2 — "⭐ Запази като любима" → snapshot_type='favorite_food'
  const handleSaveAsFavorite = async (e) => {
    e.stopPropagation();
    if (favSaving || favSaved) return;
    setFavSaving(true);
    try {
      const p = entry.protein || 0;
      const c = entry.carbs || 0;
      const fi = entry.fiber || 0;
      const dil = computeDil(p, c, fi);
      // груба анти-възпалителна оценка: висок omega-3 индикатор не е директно
      // достъпен тук, използваме fiber>=5g + low DIL като прокси.
      const antiInflammatory = fi >= 5 && dil <= 15;
      await axios.post(`${API}/health-intelligence/snapshot`, {
        snapshot_type: "favorite_food",
        title: translateFoodName(entry.food_name, language),
        summary: bg
          ? `Любима храна — DIL ${dil.toFixed(1)}g, фибри ${fi.toFixed(0)}g`
          : `Favorite food — DIL ${dil.toFixed(1)}g, fiber ${fi.toFixed(0)}g`,
        payload: {
          food_name: entry.food_name,
          dil: Number(dil.toFixed(1)),
          protein_g: p, carbs_g: c, fat_g: entry.fat || 0, fiber_g: fi,
          calories: entry.calories || 0,
          anti_inflammatory: antiInflammatory,
          source: "meal_log",
        },
        tags: [mealType?.id, fi >= 5 ? "high-fiber" : null, dil <= 10 ? "low-insulin" : null].filter(Boolean),
      });
      setFavSaved(true);
      toast.success(bg ? "Добавена в любими ❤" : "Added to favorites ❤");
      setTimeout(() => setFavSaved(false), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    } finally {
      setFavSaving(false);
    }
  };

  // iter460 — "Запази в Health Intelligence" → snapshot_type='food_log_entry'.
  // Saves the full per-meal context: macros + DIL + calories + grams + meal
  // type + timestamp. Health Intelligence recomputes NIS server-side from
  // these macros at view time, so we don't duplicate the formula in JS.
  const handleSaveToHealthIntelligence = async (e) => {
    e.stopPropagation();
    if (hiSaving || hiSaved) return;
    setHiSaving(true);
    try {
      const p = entry.protein || 0;
      const c = entry.carbs || 0;
      const f = entry.fat || 0;
      const fi = entry.fiber || 0;
      const dil = computeDil(p, c, fi);
      const displayName = translateFoodName(entry.food_name, language);
      await axios.post(`${API}/health-intelligence/snapshot`, {
        snapshot_type: "food_log_entry",
        title: displayName,
        summary: `${displayName} · ${Math.round(entry.calories || 0)} kcal · DIL ${dil.toFixed(1)}g`,
        payload: {
          food_name: entry.food_name,
          meal_type: mealType?.id,
          amount_g: entry.amount_g,
          calories: entry.calories || 0,
          protein_g: p,
          carbs_g: c,
          fat_g: f,
          fiber_g: fi,
          sugar_g: entry.sugar || 0,
          saturated_fat_g: entry.saturated_fat || 0,
          dil_g: Number(dil.toFixed(1)),
          logged_at: entry.logged_at || entry.created_at || new Date().toISOString(),
          source: "meal_log",
        },
        tags: [mealType?.id, "food_log"].filter(Boolean),
      });
      setHiSaved(true);
      toast.success(bg ? "Запазено в Health Intelligence" : "Saved to Health Intelligence");
      setTimeout(() => setHiSaved(false), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    } finally {
      setHiSaving(false);
    }
  };

  // Advanced-macro editing: ON by default off, user taps "Разширено" to override macros manually.
  // When OFF we keep the legacy behavior (scale by ratio). When ON we commit the explicit values.
  const [advanced, setAdvanced] = useState(false);
  const [editProtein, setEditProtein] = useState("");
  const [editCarbs, setEditCarbs] = useState("");
  const [editFat, setEditFat] = useState("");
  const [editFiber, setEditFiber] = useState("");
  const [editSatFat, setEditSatFat] = useState("");

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: entry.id,
    data: { fromMeal: mealType.id },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.35 : 1,
    transition: isDragging ? undefined : "opacity 0.2s ease",
    touchAction: "none",
  };

  const startEdit = (e) => {
    e.stopPropagation();
    setEditingId(entry.id);
    setEditAmount(String(entry.amount_g));
    setAdvanced(false);
    setEditProtein(String(entry.protein ?? 0));
    setEditCarbs(String(entry.carbs ?? 0));
    setEditFat(String(entry.fat ?? 0));
    setEditFiber(String(entry.fiber ?? 0));
    setEditSatFat(String(entry.saturated_fat ?? 0));
  };

  const saveEdit = () => {
    const newAmount = parseFloat(editAmount);
    if (!newAmount || newAmount <= 0) { setEditingId(null); return; }

    if (advanced) {
      // Trust user values; recompute calories from macros (4-4-9 rule).
      const p = Math.max(0, parseFloat(editProtein) || 0);
      const c = Math.max(0, parseFloat(editCarbs) || 0);
      const f = Math.max(0, parseFloat(editFat) || 0);
      const fi = Math.max(0, parseFloat(editFiber) || 0);
      const sf = Math.max(0, parseFloat(editSatFat) || 0);
      onEdit(entry.id, {
        amount_g: newAmount,
        calories: Math.round((p * 4 + c * 4 + f * 9) * 10) / 10,
        protein: Math.round(p * 10) / 10,
        carbs: Math.round(c * 10) / 10,
        fat: Math.round(f * 10) / 10,
        fiber: Math.round(fi * 10) / 10,
        sugar: Math.round((entry.sugar || 0) * 10) / 10,
        saturated_fat: Math.round(sf * 10) / 10,
      });
    } else {
      // Legacy path: proportional scaling by amount ratio.
      const ratio = newAmount / entry.amount_g;
      onEdit(entry.id, {
        amount_g: newAmount,
        calories: Math.round(entry.calories * ratio * 10) / 10,
        protein: Math.round(entry.protein * ratio * 10) / 10,
        carbs: Math.round(entry.carbs * ratio * 10) / 10,
        fat: Math.round(entry.fat * ratio * 10) / 10,
        fiber: Math.round((entry.fiber || 0) * ratio * 10) / 10,
        sugar: Math.round((entry.sugar || 0) * ratio * 10) / 10,
        saturated_fat: Math.round((entry.saturated_fat || 0) * ratio * 10) / 10,
      });
    }
    setEditingId(null);
  };

  // Whole row is draggable (listeners spread on outer div). Action buttons
  // use stopPropagation on pointerDown to prevent starting a drag.
  const stopDrag = (e) => e.stopPropagation();

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`flex items-center justify-between py-1.5 px-2 rounded-lg text-xs group cursor-grab active:cursor-grabbing select-none ${isDark ? 'hover:bg-[hsl(223,47%,15%)]' : 'hover:bg-white'}`}
      data-testid={`meal-entry-${entry.id}`}
    >
      <div className="flex items-center gap-1 flex-1 min-w-0">
        <div
          className={`flex-shrink-0 p-1 rounded opacity-60 group-hover:opacity-100 ${isDark ? 'bg-[hsl(223,47%,18%)]' : 'bg-gray-100'}`}
          aria-hidden="true"
          data-testid={`drag-handle-${entry.id}`}
        >
          <GripVertical className={`w-3.5 h-3.5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <span className={`truncate block font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            {translateFoodName(entry.food_name, language)}
          </span>
          {editingId === entry.id ? (
            <div className="flex flex-col gap-1 mt-1" onPointerDown={stopDrag}>
              <div className="flex items-center gap-1">
                <input type="number" value={editAmount} onChange={(e) => setEditAmount(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !advanced) saveEdit(); if (e.key === "Escape") setEditingId(null); }}
                  onPointerDown={stopDrag}
                  className={`w-16 h-5 text-[10px] px-1 rounded border ${isDark ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  autoFocus data-testid={`edit-amount-${entry.id}`} />
                <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>g</span>
                <button onClick={() => setAdvanced(v => !v)} onPointerDown={stopDrag}
                  className={`text-[9px] px-1.5 py-0.5 rounded ${advanced ? (isDark ? 'bg-amber-500/30 text-amber-300' : 'bg-amber-100 text-amber-700') : (isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500')}`}
                  data-testid={`edit-advanced-toggle-${entry.id}`}
                  title={bg ? "Ръчна корекция на макроси" : "Manually edit macros"}>
                  {bg ? (advanced ? "Просто" : "Разширено") : (advanced ? "Simple" : "Advanced")}
                </button>
                <button onClick={saveEdit} onPointerDown={stopDrag} className="text-green-500 hover:text-green-400" data-testid={`edit-save-${entry.id}`}><Check className="w-3 h-3" /></button>
                <button onClick={() => setEditingId(null)} onPointerDown={stopDrag} className="text-red-400 hover:text-red-300" data-testid={`edit-cancel-${entry.id}`}><X className="w-3 h-3" /></button>
              </div>
              {advanced && (
                <div className={`grid grid-cols-5 gap-1 text-[9px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-testid={`edit-macros-${entry.id}`}>
                  <label className="flex flex-col items-center">
                    <span>P</span>
                    <input type="number" step="0.1" value={editProtein} onChange={(e) => setEditProtein(e.target.value)} onPointerDown={stopDrag}
                      className={`w-full h-5 px-1 rounded border text-center ${isDark ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      data-testid={`edit-protein-${entry.id}`} />
                  </label>
                  <label className="flex flex-col items-center">
                    <span>C</span>
                    <input type="number" step="0.1" value={editCarbs} onChange={(e) => setEditCarbs(e.target.value)} onPointerDown={stopDrag}
                      className={`w-full h-5 px-1 rounded border text-center ${isDark ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      data-testid={`edit-carbs-${entry.id}`} />
                  </label>
                  <label className="flex flex-col items-center">
                    <span>F</span>
                    <input type="number" step="0.1" value={editFat} onChange={(e) => setEditFat(e.target.value)} onPointerDown={stopDrag}
                      className={`w-full h-5 px-1 rounded border text-center ${isDark ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      data-testid={`edit-fat-${entry.id}`} />
                  </label>
                  <label className="flex flex-col items-center" title={bg ? "Фибри" : "Fiber"}>
                    <span>Fi</span>
                    <input type="number" step="0.1" value={editFiber} onChange={(e) => setEditFiber(e.target.value)} onPointerDown={stopDrag}
                      className={`w-full h-5 px-1 rounded border text-center ${isDark ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      data-testid={`edit-fiber-${entry.id}`} />
                  </label>
                  <label className="flex flex-col items-center" title={bg ? "Наситени мазнини" : "Saturated fat"}>
                    <span>SF</span>
                    <input type="number" step="0.1" value={editSatFat} onChange={(e) => setEditSatFat(e.target.value)} onPointerDown={stopDrag}
                      className={`w-full h-5 px-1 rounded border text-center ${isDark ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      data-testid={`edit-satfat-${entry.id}`} />
                  </label>
                </div>
              )}
            </div>
          ) : (
            <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{entry.amount_g}g</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5" onPointerDown={stopDrag}>
        <div className="text-right">
          {entry.is_supplement || entry.supplement_info ? (
            <>
              {/* iter351.H — Supplements don't contribute calories/macros; show
                  distinctive label instead so users don't conflate dosage with food intake. */}
              <span className={`font-medium text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-700'}`}>
                {bg ? "💊 Добавка" : "💊 Supplement"}
              </span>
              <div className={`text-[9px] mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {bg ? "0 kcal · без макроси" : "0 kcal · no macros"}
              </div>
            </>
          ) : (
            <>
              <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{Math.round(entry.calories)} kcal</span>
              <div className={`text-[9px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                P:{(entry.protein||0).toFixed(0)} C:{(entry.carbs||0).toFixed(0)} F:{(entry.fat||0).toFixed(0)}
                {(entry.per100g?.fiber || entry.fiber) ? ` Fi:${(entry.fiber||0).toFixed(0)}` : ""}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap justify-end">
                <span className={`text-[9px] font-medium ${isDark ? 'text-red-400' : 'text-red-500'}`}>
                  DIL {computeDil(entry.protein||0, entry.carbs||0, entry.fiber||0).toFixed(1)}
                </span>
                {/* iter960 — MDI + SI pills alongside DIL */}
                {entry.mdi_percent != null && (
                  <span
                    className="text-[9px] font-medium flex items-center gap-0.5"
                    style={{
                      color: entry.mdi_tier_color || (
                        entry.mdi_percent >= 60 ? "#10B981"
                          : entry.mdi_percent >= 30 ? "#F59E0B"
                          : "#9CA3AF"
                      ),
                    }}
                    title={
                      (bg ? "MDI · Плътност на микронутриенти" : "MDI · Micronutrient density")
                      + (entry.mdi_source === "computed" ? (bg ? " (макро-приблизителен)" : " (macro-only proxy)") : "")
                    }
                    data-testid={`meallog-entry-mdi-${entry.id}`}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full inline-block"
                      style={{
                        background: entry.mdi_tier_color || (
                          entry.mdi_percent >= 60 ? "#10B981"
                            : entry.mdi_percent >= 30 ? "#F59E0B"
                            : "#9CA3AF"
                        ),
                      }}
                    />
                    MDI {Number(entry.mdi_percent).toFixed(0)}
                  </span>
                )}
                {entry.satiety_index != null && (
                  <span
                    className="text-[9px] font-medium flex items-center gap-0.5"
                    style={{
                      color: entry.satiety_color || (
                        entry.satiety_index >= 130 ? "#10B981"
                          : entry.satiety_index >= 80 ? "#F59E0B"
                          : "#EF4444"
                      ),
                    }}
                    title={bg ? "SI · Индекс на засищане (100 ≈ бял хляб)" : "SI · Satiety Index (100 ≈ white bread)"}
                    data-testid={`meallog-entry-si-${entry.id}`}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full inline-block"
                      style={{
                        background: entry.satiety_color || (
                          entry.satiety_index >= 130 ? "#10B981"
                            : entry.satiety_index >= 80 ? "#F59E0B"
                            : "#EF4444"
                        ),
                      }}
                    />
                    SI {Number(entry.satiety_index).toFixed(0)}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
        <button onClick={startEdit} onPointerDown={stopDrag}
          className={`opacity-0 group-hover:opacity-100 transition-opacity ${isDark ? 'text-gray-500 hover:text-[#0EA5E9]' : 'text-gray-400 hover:text-blue-500'}`}
          data-testid={`edit-meal-${entry.id}`}>
          <Pencil className="w-3 h-3" />
        </button>
        {/* iter460 — Save this individual food to Health Intelligence */}
        <button onClick={handleSaveToHealthIntelligence} onPointerDown={stopDrag}
          disabled={hiSaving || hiSaved}
          className={`opacity-0 group-hover:opacity-100 transition-opacity ${
            hiSaved
              ? "text-violet-500"
              : (isDark ? "text-gray-500 hover:text-violet-400" : "text-gray-400 hover:text-violet-500")
          }`}
          title={bg ? "Запази в Health Intelligence" : "Save to Health Intelligence"}
          data-testid={`save-hi-meal-${entry.id}`}>
          {hiSaving ? <Loader2 className="w-3 h-3 animate-spin" /> :
            <Brain className={`w-3 h-3 ${hiSaved ? "fill-current" : ""}`} />}
        </button>
        {/* iter420.2 — save food as favorite (Health Intelligence) */}
        <button onClick={handleSaveAsFavorite} onPointerDown={stopDrag}
          disabled={favSaving || favSaved}
          className={`opacity-0 group-hover:opacity-100 transition-opacity ${
            favSaved
              ? "text-rose-500"
              : (isDark ? "text-gray-500 hover:text-rose-400" : "text-gray-400 hover:text-rose-500")
          }`}
          title={bg ? "Добави в любими (Health Intelligence)" : "Save as favorite (Health Intelligence)"}
          data-testid={`fav-meal-${entry.id}`}>
          {favSaving ? <Loader2 className="w-3 h-3 animate-spin" /> :
            <Heart className={`w-3 h-3 ${favSaved ? "fill-current" : ""}`} />}
        </button>
        <button onClick={(e) => { e.stopPropagation(); onDelete(entry.id); }} onPointerDown={stopDrag}
          className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-500 transition-opacity"
          data-testid={`delete-meal-${entry.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export function MealSection({ mealType, entries, effectiveDilData, onOpenSearch, onDeleteMeal, onEditMeal, isDark, language, variant = "desktop" }) {
  const bg = language === "bg";
  const Icon = mealType.icon;
  const mealLabel = bg ? mealType.labelBg : mealType.labelEn;
  const mealCals = entries.reduce((s, e) => s + (e.calories || 0), 0);

  // Droppable zone
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `meal-zone-${mealType.id}`,
    data: { mealType: mealType.id },
  });

  // Use effective DIL from server if available, else fallback to raw calculation
  const rawDIL = calcMealDIL(entries);
  const hasBuffer = effectiveDilData && effectiveDilData.effective !== undefined;
  const displayDIL = hasBuffer ? effectiveDilData.effective.toFixed(1) : rawDIL;
  const isBuffered = hasBuffer && effectiveDilData.buffer?.modifier < 1.0;
  const isPenalized = hasBuffer && effectiveDilData.penalty?.modifier > 1.0;

  const [showBufferInfo, setShowBufferInfo] = useState(false);

  return (
    <div
      ref={setDropRef}
      className={`space-y-1 rounded-xl transition-colors ${
        isOver
          ? (isDark ? 'ring-2 ring-[#0EA5E9] bg-[#0EA5E9]/5' : 'ring-2 ring-[#0EA5E9] bg-[#0EA5E9]/5')
          : ''
      }`}
      data-testid={`meal-section-${mealType.id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2 flex-wrap gap-y-1">
          <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: `${mealType.color}20` }}>
            <Icon className="w-3.5 h-3.5" style={{ color: mealType.color }} />
          </div>
          <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-[#1A1A2E]'}`}>
            {mealLabel}
          </span>
          {mealCals > 0 && (
            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
              {Math.round(mealCals)} kcal
            </span>
          )}
          {displayDIL != null && (
            <div className="relative inline-flex items-center">
              <span
                className={`text-[10px] font-bold px-1.5 py-0.5 rounded cursor-pointer ${
                  isBuffered
                    ? (isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-600')
                    : isPenalized
                      ? (isDark ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-50 text-orange-600')
                      : (isDark ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-500')
                }`}
                data-testid={`meal-dil-${mealType.id}`}
                onClick={() => hasBuffer && setShowBufferInfo(!showBufferInfo)}
              >
                DIL {displayDIL}
                {isBuffered && <span className="ml-0.5 text-[8px] opacity-70">{String.fromCharCode(0x25BC)}</span>}
                {isPenalized && <span className="ml-0.5 text-[8px] opacity-70">{String.fromCharCode(0x25B2)}</span>}
              </span>
              {hasBuffer && rawDIL && parseFloat(displayDIL) !== parseFloat(rawDIL) && (
                <span className={`text-[9px] ml-1 line-through opacity-50 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {rawDIL}
                </span>
              )}
              {hasBuffer && (
                <button
                  className={`ml-0.5 opacity-50 hover:opacity-100 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                  onClick={(e) => { e.stopPropagation(); setShowBufferInfo(!showBufferInfo); }}
                  data-testid={`buffer-info-${mealType.id}`}
                >
                  <Info className="w-3 h-3" />
                </button>
              )}

              {/* Buffer/Penalty tooltip */}
              {showBufferInfo && hasBuffer && (
                <div
                  className={`absolute top-full left-0 mt-1 z-50 rounded-lg p-2.5 shadow-xl border text-[10px] min-w-[200px] ${isDark ? 'bg-[hsl(222,47%,11%)] border-[hsl(223,47%,20%)] text-gray-300' : 'bg-white border-gray-200 text-gray-600'}`}
                  data-testid={`buffer-tooltip-${mealType.id}`}
                >
                  <div className="flex justify-between items-center mb-1.5">
                    <span className={`font-semibold text-[11px] ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {bg ? "DIL Буфери" : "DIL Buffers"}
                    </span>
                    <button onClick={() => setShowBufferInfo(false)} className={isDark ? 'text-gray-500' : 'text-gray-400'}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="space-y-1">
                    <div className={`flex justify-between ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      <span>{bg ? "Суров DIL" : "Raw DIL"}</span>
                      <span className="font-medium">{effectiveDilData.raw}</span>
                    </div>
                    {effectiveDilData.buffer?.modifier < 1.0 && (
                      <div className="text-green-500">
                        <div className="flex justify-between">
                          <span>{bg ? "Буфер" : "Buffer"}</span>
                          <span className="font-medium">{String.fromCharCode(0x00D7)}{effectiveDilData.buffer.modifier}</span>
                        </div>
                        {effectiveDilData.buffer.reasons?.map((r, i) => (
                          <div key={i} className="pl-2 opacity-80">+ {r}</div>
                        ))}
                      </div>
                    )}
                    {effectiveDilData.penalty?.modifier > 1.0 && (
                      <div className="text-orange-500">
                        <div className="flex justify-between">
                          <span>{bg ? "Наказание" : "Penalty"}</span>
                          <span className="font-medium">{String.fromCharCode(0x00D7)}{effectiveDilData.penalty.modifier}</span>
                        </div>
                        {effectiveDilData.penalty.reasons?.map((r, i) => (
                          <div key={i} className="pl-2 opacity-80">+ {r}</div>
                        ))}
                      </div>
                    )}
                    <div className={`flex justify-between pt-1 border-t font-semibold ${isDark ? 'border-gray-700 text-white' : 'border-gray-200 text-gray-800'}`}>
                      <span>{bg ? "Ефективен DIL" : "Effective DIL"}</span>
                      <span>{effectiveDilData.effective}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <Button variant="ghost" size="icon" className={`h-7 w-7 rounded-lg ${isDark ? 'hover:bg-[hsl(223,47%,18%)]' : 'hover:bg-gray-100'}`}
          onClick={() => onOpenSearch(mealType.id)} data-testid={`add-meal-${mealType.id}${variant === "mobile" ? "-m" : ""}`}>
          <Plus className="w-4 h-4 text-[#0EA5E9]" />
        </Button>
      </div>

      {entries.length > 0 && (
        <div className="space-y-0.5">
          {entries.map((entry) => (
            <DraggableMealEntry
              key={entry.id}
              entry={entry}
              mealType={mealType}
              isDark={isDark}
              language={language}
              onDelete={onDeleteMeal}
              onEdit={onEditMeal}
            />
          ))}
        </div>
      )}

      {entries.length === 0 && (
        <button onClick={() => onOpenSearch(mealType.id)}
          className={`w-full py-2.5 text-xs text-center rounded-lg border-dashed border transition-colors ${
            isOver
              ? (isDark ? 'border-[#0EA5E9] text-[#0EA5E9]' : 'border-[#0EA5E9] text-[#0EA5E9]')
              : (isDark ? 'border-gray-700 text-gray-500 hover:border-gray-600 hover:text-gray-400' : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-500')
          }`}>
          {isOver ? (bg ? "Пуснете тук" : "Drop here") : `+ ${bg ? "Добавяне на храна" : "Add food"}`}
        </button>
      )}
    </div>
  );
}
